import React, { useState, useEffect } from "react";
import API from "../api";
import { Card, Badge, Button, Toast, Loading, Empty, SectionHeader, Input } from "../components/UI";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", role: "client", clientId: "" });
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [usersRes, clientsRes] = await Promise.all([
        API.get("/auth/users"),
        API.get("/clients"),
      ]);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const addUser = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.post("/auth/create-user", form);
      setToast({ message: "User created!", type: "success" });
      setForm({ email: "", password: "", role: "client", clientId: "" });
      setShowAdd(false);
      fetchAll();
    } catch (err) {
      setToast({ message: err.response?.data?.error || "Failed", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await API.delete(`/auth/user/${id}`);
      setUsers(p => p.filter(u => u._id !== id));
      setToast({ message: "User deleted", type: "success" });
    } catch {
      setToast({ message: "Failed", type: "error" });
    }
  };

  const resetPassword = async () => {
    if (!newPassword) return;
    setSaving(true);
    try {
      await API.post("/auth/admin-reset", { userId: resetUser._id, newPassword });
      setToast({ message: "Password reset ho gaya!", type: "success" });
      setResetUser(null);
      setNewPassword("");
    } catch {
      setToast({ message: "Failed", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const getClientName = (clientId) => {
    const c = clients.find(c => c._id === clientId);
    return c ? c.name : "—";
  };

  if (loading) return <Loading />;

  const th = { textAlign: "left", padding: "12px 16px", fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", background: "var(--bg-elevated)" };
  const td = { padding: "12px 16px", fontSize: 13, verticalAlign: "middle" };

  return (
    <div className="fade-in">
      <SectionHeader
        title="User Management"
        subtitle={`${users.length} user${users.length !== 1 ? "s" : ""} registered`}
        action={
          <Button onClick={() => setShowAdd(!showAdd)}>
            {showAdd ? "✕ Cancel" : "⊕ Add User"}
          </Button>
        }
      />

      {/* Add User Form */}
      {showAdd && (
        <Card style={{ marginBottom: 20 }}>
          <form onSubmit={addUser} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", fontFamily: "var(--font-mono)" }}>
              New User
            </div>

            <Input
              label="Email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="client@email.com"
              required
            />

            <Input
              label="Password"
              name="password"
              value={form.password}
              onChange={handleChange}
              placeholder="Strong password"
              type="password"
              required
            />

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={labelStyle}>Role</label>
              <select name="role" value={form.role} onChange={handleChange} style={selectStyle}>
                <option value="client">Client</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {form.role === "client" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={labelStyle}>Client Account</label>
                <select name="clientId" value={form.clientId} onChange={handleChange} style={selectStyle}>
                  <option value="">-- Select Client --</option>
                  {clients.map(c => (
                    <option key={c._id} value={c._id}>{c.name} ({c.brand})</option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Button variant="secondary" type="button" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Creating..." : "⊕ Create User"}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Reset Password Modal */}
      {resetUser && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
              Reset Password — {resetUser.email}
            </div>
            <Input
              label="New Password"
              name="newPassword"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Naya password"
              type="password"
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
              <Button variant="secondary" onClick={() => { setResetUser(null); setNewPassword(""); }}>Cancel</Button>
              <Button onClick={resetPassword} disabled={saving || !newPassword}>
                {saving ? "Resetting..." : "Reset Password"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      {users.length === 0 ? (
        <Card><Empty message="No users yet." icon="👤" /></Card>
      ) : (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)" }}>
                {["Email", "Role", "Client", "Created", "Actions"].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((user, i) => (
                <tr key={user._id} style={{ borderBottom: "1px solid var(--border)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                  <td style={td}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 6, background: "var(--bg-elevated)", border: "1px solid var(--border-light)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>
                        {user.role === "admin" ? "👑" : "👤"}
                      </div>
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{user.email}</span>
                    </div>
                  </td>
                  <td style={td}>
                    <Badge color={user.role === "admin" ? "orange" : "blue"}>
                      {user.role}
                    </Badge>
                  </td>
                  <td style={{ ...td, color: "var(--text-secondary)" }}>
                    {user.clientId ? getClientName(user.clientId) : "—"}
                  </td>
                  <td style={{ ...td, fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                    {new Date(user.createdAt).toLocaleDateString("en-IN")}
                  </td>
                  <td style={td}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        onClick={() => setResetUser(user)}
                        title="Reset Password"
                        style={iconBtn}
                      >
                        🔑
                      </button>
                      <button
                        onClick={() => deleteUser(user._id)}
                        title="Delete"
                        style={{ ...iconBtn, color: "var(--accent-red)" }}
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

const labelStyle = { fontSize: 12, color: "var(--text-secondary)", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" };
const selectStyle = { background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "10px 14px", color: "var(--text-primary)", fontSize: 14, fontFamily: "var(--font-body)", outline: "none", width: "100%" };
const iconBtn = { background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 6, width: 28, height: 28, cursor: "pointer", fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", justifyContent: "center" };
const modalOverlay = { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" };
const modalBox = { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: 28, width: "100%", maxWidth: 400, boxShadow: "var(--shadow)" };
